import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  X, 
  CheckCircle, 
  ChevronRight,
  Building,
  Table
} from 'lucide-react';
import axios from 'axios';

const OrderAtTable = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('business'); // 'business', 'table', 'menu'
  const [businessInfo, setBusinessInfo] = useState(null);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBusinessModal, setShowBusinessModal] = useState(true);
  const [showTableModal, setShowTableModal] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const primaryColor = 'var(--theme-primary)';

  useEffect(() => {
    fetchBusinessInfo();
    fetchTables();
  }, []);

  const fetchBusinessInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/business-info`);
      setBusinessInfo(response.data);
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/tables/public`);
      console.log('📊 Fetched tables:', response.data);
      if (response.data.success) {
        setTables(response.data.tables || []);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmBusiness = () => {
    setShowBusinessModal(false);
    setShowTableModal(true);
    setStep('table');
  };

  const handleSelectTable = (table) => {
    setSelectedTable(table);
  };

  const handleConfirmTable = () => {
    if (!selectedTable) return;
    
    // Store table info in sessionStorage for the ordering flow
    sessionStorage.setItem('orderAtTable', JSON.stringify({
      tableId: selectedTable.id,
      tableName: selectedTable.table_name,
      orderType: 'orderAtTable'
    }));
    
    // Navigate to the order page with the order type parameter
    navigate('/order-online?type=orderAtTable');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Business Confirmation Modal */}
      {showBusinessModal && businessInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Confirm Business Location
                </h3>
                <button
                  onClick={() => navigate('/')}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {businessInfo.website_title || businessInfo.hospital_name || 'Restaurant'}
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  {businessInfo.website_subtitle || 'Welcome to our establishment'}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-theme-blue flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Location</p>
                    <p className="text-sm text-gray-700 mt-1">{businessInfo.address || 'Address not available'}</p>
                    {businessInfo.phone && (
                      <p className="text-sm text-gray-600 mt-1">Phone: {businessInfo.phone}</p>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 text-center">
                Are you dining at this location?
              </p>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmBusiness}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2"
                style={{ backgroundColor: primaryColor }}
              >
                <CheckCircle className="h-4 w-4" />
                Yes, Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Selection Modal */}
      {showTableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200" style={{ backgroundColor: primaryColor }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Table className="h-5 w-5" />
                  Select Your Table
                </h3>
                <button
                  onClick={() => navigate('/')}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {loading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: primaryColor }}></div>
                </div>
              ) : tables.length === 0 ? (
                <div className="text-center py-8">
                  <Building className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No tables available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">
                    Please select your table number to continue with your order.
                  </p>
                  
                  {tables.map((table) => (
                    <div
                      key={table.id}
                      onClick={() => handleSelectTable(table)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedTable?.id === table.id
                          ? 'border-theme-blue bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">{table.table_name}</h4>
                          <p className="text-sm text-gray-600 mt-1">
                            Capacity: {table.capacity} {table.capacity === 1 ? 'person' : 'people'}
                          </p>
                        </div>
                        {selectedTable?.id === table.id && (
                          <CheckCircle className="h-6 w-6 text-theme-blue" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowTableModal(false);
                  setShowBusinessModal(true);
                  setStep('business');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleConfirmTable}
                disabled={!selectedTable}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: primaryColor }}
              >
                Continue to Menu
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderAtTable;
